import React, {useCallback, useEffect, useState} from "react";
import {Button, Col, Input, Modal, Row, Select, Space, Table, Typography} from "antd";
import {useMediaQuery} from "react-responsive";
import {motion} from "framer-motion";
import {CheckCircleOutlined, EditOutlined, SearchOutlined, UndoOutlined} from "@ant-design/icons";
import theme from "../theme";
import peopleService from "../services/PeopleService";
import rollCallService from "../services/RollCallService";
import {useSelector} from "react-redux";
import PersonEditForm from "../components/people/PersonEditForm";
import formatDate from "../utils/dateUtils";

const {Title, Text} = Typography;
const {Option} = Select;

const PeoplePage = () => {
    const [people, setPeople] = useState([]);
    const [rollCallLoading, setRollCallLoading] = useState({});
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({current: 1, pageSize: 10, total: 0});
    const [selectedPerson, setSelectedPerson] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalMode, setModalMode] = useState(null);

    const isMobile = useMediaQuery({maxWidth: 800});
    const auth = useSelector((state) => state.auth || {});
    const userSections = auth.user?.sections || [];

    const [modal, contextHolderMo] = Modal.useModal();

    const formatAddress = (record) => {
        const street = record.street || '';
        const exteriorNumber = record.exteriorNumber || '';
        const interiorNumber = record.interiorNumber || '';
        const neighborhood = record.neighborhood || '';
        const zipCode = record.zipCode || '';
        
        const number = exteriorNumber && interiorNumber 
            ? `#${exteriorNumber}-${interiorNumber}`
            : exteriorNumber 
                ? `#${exteriorNumber}`
                : interiorNumber 
                    ? `#${interiorNumber}`
                    : '';
        
        return `${street} ${number}, ${neighborhood}, CP ${zipCode}`.trim();
    };

    const [filters, setFilters] = useState({
        createdBy: {value: auth.user?.username, operator: "eq"},
        name: {value: "", operator: "like"},
        firstLastName: {value: "", operator: "like"},
        secondLastName: {value: "", operator: "like"},
        phone: {value: "", operator: "like"},
        sectionId: {value: [], operator: "in"},
    });

    const [tempFilters, setTempFilters] = useState({
        name: "",
        firstLastName: "",
        secondLastName: "",
        phone: "",
        sectionId: [],
    });

    const fetchPeople = useCallback(async (page, pageSize, appliedFilters) => {
        setLoading(true);
        try {
            const response = await peopleService.getAll(page, pageSize, appliedFilters);
            setPeople(response.data);
            setPagination((prev) => ({
                ...prev,
                current: page,
                pageSize,
                total: response.meta.totalItems,
            }));
        } catch (error) {
            console.error("Error al cargar registros:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const initialFilters = {
            createdBy: {value: auth.user?.username, operator: "eq"},
            name: {value: "", operator: "like"},
            firstLastName: {value: "", operator: "like"},
            secondLastName: {value: "", operator: "like"},
            phone: {value: "", operator: "like"},
            sectionId: {value: [], operator: "in"},
        };
        fetchPeople(1, 10, initialFilters).catch(console.error);
    }, [auth.user?.username, fetchPeople]);

    const handleTableChange = (newPagination) => {
        fetchPeople(newPagination.current, newPagination.pageSize, filters).catch(console.error);
    };

    const handleFilterChange = (key, value) => {
        setTempFilters((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const applyFilters = () => {
        const newFilters = {
            createdBy: {value: auth.user?.username, operator: "eq"},
            name: {value: tempFilters.name, operator: "like"},
            firstLastName: {value: tempFilters.firstLastName, operator: "like"},
            secondLastName: {value: tempFilters.secondLastName, operator: "like"},
            phone: {value: tempFilters.phone, operator: "like"},
            sectionId: {value: tempFilters.sectionId, operator: "in"},
        };
        setFilters(newFilters);
        fetchPeople(1, pagination.pageSize, newFilters);
    };

    const resetFilters = () => {
        const resetTempFilters = {
            name: "",
            firstLastName: "",
            secondLastName: "",
            phone: "",
            sectionId: [],
        };
        const resetAppliedFilters = {
            createdBy: {value: auth.user?.username, operator: "eq"},
            name: {value: "", operator: "like"},
            firstLastName: {value: "", operator: "like"},
            secondLastName: {value: "", operator: "like"},
            phone: {value: "", operator: "like"},
            sectionId: {value: [], operator: "in"},
        };
        setTempFilters(resetTempFilters);
        setFilters(resetAppliedFilters);
        fetchPeople(1, pagination.pageSize, resetAppliedFilters).catch(console.error);
    };

    const handleViewClick = (record) => {
        setSelectedPerson(record);
        setModalMode("view");
        setIsModalVisible(true);
    };

    const handleEditClick = (record) => {
        setSelectedPerson(record);
        setModalMode("edit");
        setIsModalVisible(true);
    };

    const handleCloseModal = () => {
        setIsModalVisible(false);
        setSelectedPerson(null);
        setModalMode(null);
        fetchPeople(pagination.current, pagination.pageSize, filters).catch(console.error);
    };

    const columns = [
        {
            title: "Acciones",
            key: "actions",
            render: (record) => (
                <Space size="middle">
                    {!record.attendanceAt && <Button
                        type="default"
                        icon={<EditOutlined/>}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEditClick(record);
                        }}
                        size="small"
                    >
                        Editar
                    </Button>}
                    <Button
                        hidden
                        type={record.attendanceAt ? "default" : "primary"}
                        icon={<CheckCircleOutlined/>}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleRollCallClick(record);
                        }}
                        size="small"
                        loading={rollCallLoading[record.id]}
                        disabled={!!record.attendanceAt}
                    >
                        {record.attendanceAt ? "Asistencia Marcada" : "Pase de Lista"}
                    </Button>
                </Space>
            ),
        },
        {title: "Clave de Elector", dataIndex: "electorKey", key: "electorKey"},
        {
            title: "Nombre",
            key: "fullName",
            render: (record) => `${record.name} ${record.firstLastName} ${record.secondLastName || ""}`,
        },
        {title: "Teléfono", dataIndex: "phone", key: "phone"},
        {
            title: "Dirección",
            key: "address",
            render: (record) => formatAddress(record),
        },
        {title: "Sección", dataIndex: "sectionId", key: "sectionId"},
        {
            title: "Fecha de creación",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (createdAt) => formatDate(createdAt),
        },
        {
            title: "Fecha de actualización",
            dataIndex: "updatedAt",
            key: "updatedAt",
            render: (updatedAt) => formatDate(updatedAt),
        }
    ];

    const mobileColumns = [
        {
            title: "Nombre",
            key: "fullName",
            render: (record) => (
                <div>
                    <div>{`${record.name} ${record.firstLastName}`}</div>
                    <div style={{fontSize: '12px', color: '#666'}}>
                        Sección: {record.sectionId}
                    </div>
                </div>
            ),
        },
        {
            title: "Acciones",
            key: "actions",
            render: (record) => (
                <Space direction="vertical" size="small">
                    {!record.attendanceAt && <Button
                        type="default"
                        icon={<EditOutlined/>}
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEditClick(record);
                        }}
                        block
                    >
                        Editar
                    </Button>}
                    <Button
                        hidden
                        type={record.attendanceAt ? "default" : "primary"}
                        icon={<CheckCircleOutlined/>}
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleRollCallClick(record);
                        }}
                        loading={rollCallLoading[record.id]}
                        disabled={!!record.attendanceAt}
                        block
                    >
                        {record.attendanceAt ? "Marcada" : "Pase de Lista"}
                    </Button>
                </Space>
            ),
        },
    ];

    // Función para manejar el pase de lista
    const handleRollCallClick = (record) => {
        const fullName = `${record.name} ${record.firstLastName} ${record.secondLastName || ""}`.trim();

        modal.confirm({
            title: 'Confirmar Pase de Lista',
            content: (
                <div>
                    <p>¿Está seguro de que desea marcar asistencia para?</p>
                    <p><strong>{fullName}</strong></p>
                    <p>Clave de Elector: <strong>{record.electorKey}</strong></p>
                    <p>Sección: <strong>{record.sectionId}</strong></p>
                </div>
            ),
            okText: 'Sí, marcar asistencia',
            cancelText: 'Cancelar',
            okType: 'primary',
            onOk: async () => {
                await handleMarkAttendance(record);
            },
        });
    };

    const handleMarkAttendance = async (record) => {
        const recordId = record.id;
        setRollCallLoading(prev => ({
            ...prev,
            [recordId]: true
        }));

        try {
            const rollCallData = {
                electorKey: record.electorKey,
                entityId: auth.user?.entityId,
                sectionId: record.sectionId
            };

            const response = await rollCallService.create(rollCallData);
            const fullName = `${record.name} ${record.firstLastName} ${record.secondLastName || ""}`.trim();
            modal.success({
                title: 'Asistencia marcada exitosamente para',
                content: (
                    <div>
                        <p>¿Está seguro de que desea marcar asistencia para?</p>
                        <p><strong>{fullName}</strong></p>
                        <p>Clave de Elector: <strong>{record.electorKey}</strong></p>
                        <p>Sección: <strong>{record.sectionId}</strong></p>
                    </div>
                ),
            });
            setPeople(prevPeople => {
                return prevPeople.map(person =>
                    person.electorKey === response.data.electorKey
                        ? {...person, attendanceAt: response.data.createdAt}
                        : person
                );
            });
        } catch (error) {
            console.error("Error al marcar asistencia:", error);
            let message = 'Error inesperado al marcar asistencia';
            if (error.details && typeof error.details === 'string') {
                message = error.details;
            } else if (error.details && Array.isArray(error.details)) {
                message = error.details.join(', ');
            }
            modal.error({
                title: 'Error al marcar asistencia.',
                content: message
            });
        } finally {
            setRollCallLoading(prev => ({
                ...prev,
                [recordId]: false
            }));
        }
    };

    return (
        <>
            {contextHolderMo}
            <motion.div
                initial={{opacity: 0, y: -10}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.5}}
                style={{
                    maxWidth: isMobile ? "100%" : "90%",
                    width: "90vw",
                    margin: "20px auto",
                    background: theme.colors.card,
                    padding: "20px",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    overflowX: "auto",
                }}
            >
                <Title level={2} style={{textAlign: "center"}}>
                    Registros de Convencidos
                </Title>

                <Row gutter={[16, 16]} style={{marginBottom: "20px"}}>
                    <Col xs={24} sm={12} md={8}>
                        <Input
                            placeholder="Nombre"
                            value={tempFilters.name}
                            onChange={(e) => handleFilterChange("name", e.target.value)}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Input
                            placeholder="Apellido Paterno"
                            value={tempFilters.firstLastName}
                            onChange={(e) => handleFilterChange("firstLastName", e.target.value)}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Input
                            placeholder="Apellido Materno"
                            value={tempFilters.secondLastName}
                            onChange={(e) => handleFilterChange("secondLastName", e.target.value)}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Input
                            placeholder="Teléfono"
                            value={tempFilters.phone}
                            onChange={(e) => handleFilterChange("phone", e.target.value)}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Select
                            mode="multiple"
                            placeholder="Sección"
                            value={tempFilters.sectionId}
                            onChange={(value) => handleFilterChange("sectionId", value)}
                            style={{width: "100%"}}
                        >
                            {userSections.map((sec) => (
                                <Option key={sec} value={sec}>{`Sección ${sec}`}</Option>
                            ))}
                        </Select>
                    </Col>
                </Row>

                <div style={{display: "flex", justifyContent: "flex-end", marginBottom: "20px"}}>
                    <Space>
                        <Button type="primary" icon={<SearchOutlined/>} onClick={applyFilters}>
                            Aplicar Filtros
                        </Button>
                        <Button icon={<UndoOutlined/>} onClick={resetFilters}>Restablecer</Button>
                    </Space>
                </div>

                <Table
                    columns={isMobile ? mobileColumns : columns}
                    dataSource={people}
                    rowKey="id"
                    pagination={{...pagination, showSizeChanger: true, pageSizeOptions: ["1", "5", "10", "20"]}}
                    loading={loading}
                    onChange={handleTableChange}
                    scroll={{x: "max-content"}}
                    onRow={(record) => ({
                        onClick: () => handleViewClick(record),
                        style: {cursor: "pointer"},
                    })}
                />

                <Text style={{textAlign: "right", display: "block", marginTop: "10px"}}>
                    Mostrando {people.length} de {pagination.total} registros
                </Text>
            </motion.div>

            {selectedPerson && (
                <Modal
                    open={isModalVisible}
                    onCancel={handleCloseModal}
                    footer={null}
                    width={isMobile ? "90%" : "45%"}
                >
                    <PersonEditForm person={selectedPerson} mode={modalMode} onClose={handleCloseModal}/>
                </Modal>
            )}
        </>
    );
};

export default PeoplePage;